import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';

import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { EntityServiceFactory } from '@factories/entity-service-factory/entity-service-factory';
import { ICustomInfoEntity } from '@http/clients/custom-info-entity';
import { ISearchCustomInfoEntityParameters } from '@http/clients/search-custom-info-entity-parameters';
import { CustomInfoFormGroup, CustomInfoFormValue, ICustomizationFilters } from '@models/customization.types';
import { Condition } from '@models/enums/Condition';
import { AwaiterService } from '@services/awaiter.service';
import { CustomizationService } from '@services/http/clients/customization.service';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { ReactiveFieldInputComponent } from '@shared/reactive-form-controls/reactive-field-input/reactive-field-input.component';
import {
    HelperInfoComponent,
    IconSanitizerService,
    ModalService,
    NoDataComponent,
    ObjectExtensionsService,
    SnackbarNotificationService
} from 'ngx-lib';
import { CustomizationFiltersComponent } from '../customization-filters/customization-filters.component';

@Component({
    selector: 'app-customization-custom-info',
    templateUrl: './customization-custom-info.component.html',
    styleUrls: ['./customization-custom-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ReactiveFormsModule,
        DragDropModule,
        ReactiveFieldInputComponent,
        HelperInfoComponent,
        NoDataComponent,
        CustomizationFiltersComponent
    ]
})
export class CustomizationCustomInfoComponent {
    private readonly snackbar = inject(SnackbarNotificationService);
    private readonly iconSanitizer = inject(IconSanitizerService);
    private readonly entityServiceFactory = inject(EntityServiceFactory);
    private readonly awaiter = inject(AwaiterService);
    private readonly modalService = inject(ModalService);
    private readonly objectExtensionService = inject(ObjectExtensionsService);
    private readonly customizationService = inject(CustomizationService);
    private readonly service = signal<ReturnType<typeof this.entityServiceFactory.getService> | undefined>(undefined);

    readonly entityName = input.required<string>();
    readonly entityTypeId = input.required<number>();
    readonly filters = signal<ICustomizationFilters>({});

    readonly hasUnsavedChanges = output<boolean>();

    readonly canAddNewInfo = computed(() => {
        return this.customizationService.canAddNew(this.filters());
    });

    readonly dragIndicatorIcon: SafeHtml = this.iconSanitizer.getIcon('dragIndicator');
    readonly archiveIcon: SafeHtml = this.iconSanitizer.getIcon('archive');
    readonly restoreIcon: SafeHtml = this.iconSanitizer.getIcon('restore');
    readonly lockedIcon: SafeHtml = this.iconSanitizer.getIcon('locked');
    readonly plusIcon: SafeHtml = this.iconSanitizer.getIcon('plus');

    readonly customInfoForm = new FormGroup({
        infoItems: new FormArray<CustomInfoFormGroup>([])
    });

    readonly customInfoArray = this.customInfoForm.controls.infoItems;

    readonly infoDirty = computed(() => {
        const current = (this.formValueSignal().infoItems ?? []) as CustomInfoFormValue[];
        const initial = this.initialInfo();
        return !this.customizationService.areInfoEqual(current, initial);
    });

    readonly hasNoCustomInfo = computed(() => {
        if (!this.hasLoadedInitially()) return false;
        const formValue = this.formValueSignal();
        const infoItems = formValue?.infoItems as CustomInfoFormValue[] | undefined;
        return !infoItems || infoItems.length === 0;
    });

    private readonly initialInfo = signal<CustomInfoFormValue[]>([]);
    private readonly currentEntityTypeId = signal<number | null>(null);
    private readonly hasLoadedInitially = signal<boolean>(false);
    private readonly formValueSignal = toSignal(this.customInfoForm.valueChanges, {
        initialValue: this.customInfoForm.value
    });

    constructor() {
        effect(async () => {
            const entityTypeId = this.entityTypeId();
            if (!entityTypeId) return;

            if (this.currentEntityTypeId() === entityTypeId) return;

            this.customInfoArray.clear();
            this.initialInfo.set([]);
            this.customInfoForm.markAsPristine();
            this.customInfoForm.updateValueAndValidity({ emitEvent: false });
            this.hasLoadedInitially.set(false);

            await this.awaiter.awaitAction('Loading Custom Information', async () => {
                const archiveOptions = await this.customizationService.getArchiveOptions();
                const defaultActiveStatus = archiveOptions?.find((s) => s.id === Condition.Active);

                this.filters.set(defaultActiveStatus ? { status: defaultActiveStatus } : {});
                this.currentEntityTypeId.set(entityTypeId);
                this.service.set(this.entityServiceFactory.getService(entityTypeId));
            });
        });

        effect(() => {
            this.filters();
            const service = this.service();

            if (!service) return;

            this.awaiter.awaitAction('Loading Custom Information', async () => {
                await this.loadCustomInfo();
            });
        });

        effect(() => {
            this.hasUnsavedChanges.emit(this.infoDirty());
        });
    }

    async onFiltersChange(filters: ICustomizationFilters): Promise<void> {
        this.filters.set(filters);
    }

    addCustomInfo(): void {
        if (!this.canAddNewInfo()) return;

        this.customInfoArray.insert(0, this.createCustomInfoGroup());
        this.customInfoForm.markAsDirty();
    }

    onInfoDrop(event: CdkDragDrop<CustomInfoFormGroup[]>): void {
        if (event.previousIndex === event.currentIndex) return;
        const control = this.customInfoArray.at(event.previousIndex);
        if (!control) return;
        this.customInfoArray.removeAt(event.previousIndex);
        this.customInfoArray.insert(event.currentIndex, control);
        this.customInfoForm.markAsDirty();
    }

    async onSaveCustomInfo(): Promise<void> {
        if (this.customInfoForm.invalid) {
            this.customInfoForm.markAllAsTouched();
            return;
        }

        await this.awaiter.awaitAction('Saving Custom Information', async () => {
            const currentFields = this.transformFormValuesForSave();
            if (currentFields.length) {
                const currentService = this.service();
                if (!currentService) return;

                const savedCustomInfo = await currentService.save(currentFields);
                const savedFormValues = this.mapCustomInfoToFormValues(savedCustomInfo);

                this.resetCustomInfoForm(savedFormValues);
                this.initialInfo.set(
                    savedFormValues.map((item) => this.objectExtensionService.clone(item) as CustomInfoFormValue)
                );

                this.snackbar.success('Custom Information saved successfully.');
            }
        });
    }

    async onToggleCondition(index: number, archive: boolean): Promise<void> {
        const control = this.customInfoArray.at(index);
        if (!control) return;
        const id = control.controls.id.value;
        if (!id || id <= 0) return;

        const currentService = this.service();
        if (!currentService) return;

        const actionLabel = archive ? 'Archiving Custom Information' : 'Restoring Custom Information';
        const successMessage = archive
            ? `${control.controls.name.value ?? 'Item'} archived successfully.`
            : `${control.controls.name.value ?? 'Item'} restored successfully.`;

        await this.awaiter.awaitAction(actionLabel, async () => {
            if (archive) {
                await currentService.deactivate(id);
            } else {
                await currentService.activate(id);
            }

            const filters = this.filters();
            const shouldRemove =
                (archive && filters.status?.id === Condition.Active) ||
                (!archive && filters.status?.id === Condition.Archived);

            if (shouldRemove) {
                this.customInfoArray.removeAt(index);
            } else {
                control.controls.isActive.setValue(!archive);
            }

            const currentValues = (this.customInfoForm.getRawValue().infoItems ?? []) as CustomInfoFormValue[];
            this.initialInfo.set(
                currentValues.map((item) => this.objectExtensionService.clone(item) as CustomInfoFormValue)
            );
            this.customInfoForm.markAsPristine();
            this.customInfoForm.updateValueAndValidity({ emitEvent: false });
            this.snackbar.success(successMessage);
        });
    }

    async onCancelCustomInfo(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Cancel Editing',
            cancelCaption: 'Continue Edition',
            content: 'Are you sure you want to move on? Your changes will be lost',
            title: 'Unsaved changes'
        });

        if (!responseOk) return;

        this.resetCustomInfoForm(this.initialInfo());
    }

    private async loadCustomInfo(): Promise<void> {
        const currentService = this.service();
        if (!currentService) return;

        const searchBody = this.buildSearchConfig();
        const info = await currentService.search(searchBody);

        const formValues = this.mapCustomInfoToFormValues(info || []);
        this.resetCustomInfoForm(formValues);
        const current = (this.customInfoForm.getRawValue().infoItems ?? []) as CustomInfoFormValue[];
        this.initialInfo.set(current.map((item) => this.objectExtensionService.clone(item) as CustomInfoFormValue));
        this.hasLoadedInitially.set(true);
    }

    private buildSearchConfig(): ISearchCustomInfoEntityParameters {
        const filterValues = this.filters();
        return {
            isActive: filterValues.status?.value,
            isReserved: filterValues.source?.value
        } as ISearchCustomInfoEntityParameters;
    }

    private mapCustomInfoToFormValues(customInfo: ICustomInfoEntity[]): CustomInfoFormValue[] {
        return customInfo.map((item) => {
            const itemWithOrgId = item as ICustomInfoEntity & { organizationId?: number | null };
            return {
                id: item.id,
                name: item.name ?? '',
                isActive: item.isActive,
                isReserved: item.isReserved,
                order: item.order,
                organizationId: itemWithOrgId.organizationId
            };
        });
    }

    /*
      Transforms form values to the format expected by the backend.
      For new items (id: 0): sends id, name, isActive, and organizationId (no order, no isReserved).
      For existing items: sends id, name, isActive, order, and organizationId (no isReserved).
      Note: Backend recalculates order based on array position and calculates isReserved after save.
      We don't send isReserved because the backend recalculates it based on OrganizationId.
     */
    private transformFormValuesForSave(): ICustomInfoEntity[] {
        const formValues = (this.customInfoForm.getRawValue().infoItems ?? []) as CustomInfoFormValue[];
        return formValues.map((item) => {
            const hasId = (item.id ?? 0) > 0;
            return {
                id: hasId ? item.id! : 0,
                name: item.name,
                isActive: hasId ? item.isActive : true,
                ...(hasId && { order: item.order }),
                ...(item.organizationId !== undefined && { organizationId: item.organizationId })
            } as unknown as ICustomInfoEntity;
        });
    }

    private resetCustomInfoForm(infoItems: CustomInfoFormValue[]): void {
        this.customInfoArray.clear();
        for (const info of infoItems) {
            this.customInfoArray.push(this.createCustomInfoGroup(info));
        }
        this.customInfoForm.markAsPristine();
        this.customInfoForm.updateValueAndValidity({ emitEvent: true });
    }

    private createCustomInfoGroup(value?: CustomInfoFormValue): CustomInfoFormGroup {
        const isNewItem = !value || (value.id ?? 0) === 0;
        return new FormGroup({
            id: new FormControl<number | null>(value?.id ?? null),
            name: new FormControl<string>(value?.name ?? '', {
                nonNullable: true,
                validators: [Validators.required, Validators.maxLength(200)]
            }),
            isActive: new FormControl<boolean>(isNewItem ? true : (value?.isActive ?? false), { nonNullable: true }),
            isReserved: new FormControl<boolean | undefined>(value?.isReserved) as FormControl<boolean | undefined>,
            order: new FormControl<number>(value?.order ?? this.customInfoArray.length, {
                nonNullable: true
            }),
            organizationId: new FormControl<number | null | undefined>(value?.organizationId ?? null)
        }) as CustomInfoFormGroup;
    }
}
